// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/CheckInTodo.tsx
// 🔧 PURPOSE: "Prima di partire · Da fare" della vista B (prima
//             dell'arrivo, e il giorno dell'arrivo fino alle 15:00).
//
//             Check-in online NON fatto:
//             - finestra ancora chiusa (più di 7 giorni all'arrivo): il
//               riquadro crema "OBBLIGATORIO" con il giorno in cui si apre,
//               senza bottone;
//             - finestra aperta: il riquadro pesca "OBBLIGATORIO" (il giorno
//               dell'arrivo "DA FARE OGGI") e FAI IL CHECK-IN, verso la pagina
//               dei gruppi se è un gruppo.
//             A destra "Per 2 ospiti"; per un gruppo "1 di 3 appartamenti"
//             (fetchGroupBookingInfo: se ne tengono solo i conti, i codici
//             della risposta no, critic 33). Il "0 di 2 ospiti" del mockup non
//             esiste: il channel manager manda tutti gli ospiti insieme.
//
//             Check-in FATTO:
//             - "Codice porta pronto" e MOSTRA IL CODICE, che apre la verifica
//               dell'email e poi il codice (useDoorCode, come MyStay: l'email
//               ogni volta, il codice solo in memoria);
//             - il giorno dell'arrivo il codice sta già fra le tessere della
//               card del soggiorno: qui non si ripete;
//             - con la tassa di soggiorno ancora da pagare il codice NON è
//               pronto (lo staff lo attiva dopo il pagamento): solo la riga
//               "Check-in online · FATTO".
//
//             Tassa di soggiorno chiesta e non pagata (anche prima
//             dell'arrivo, critic 22): "DA PAGARE PRIMA DELL'ARRIVO",
//             l'importo, PAGA e la nota sul codice attivato dopo il pagamento.
//
//             Niente da fare (check-in fatto, codice nelle tessere, niente
//             tassa): la sezione non c'è.
// ============================================

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Check, KeyRound } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { HomeState } from '../../lib/homeState';
import { fetchGroupBookingInfo, type GuestBooking } from '../../lib/guestApi';
import { useDoorCode } from '../../hooks/useDoorCode';
import DoorCodeReveal from './DoorCodeReveal';
import { formatDayKey } from './format';
import TouristTaxDue from './TouristTaxDue';
import CheckInRedoNote from '../CheckInRedoNote';
import { CardShell, IconCircle, InkButton, INK_TEXT, Pill, Section, SectionTitle } from './ui';

export interface CheckInTodoProps {
  state: HomeState;
  token: string;
  booking: GuestBooking;
  /**
   * Il codice della porta è già fra le tessere della card del soggiorno (il
   * giorno dell'arrivo, a check-in fatto): qui non se ne apre un secondo.
   */
  doorInQuickActions?: boolean;
  className?: string;
}

type GroupProgress = { done: number; total: number };

/**
 * Quanti appartamenti del gruppo hanno fatto il check-in. La risposta
 * contiene anche i codici della porta di tutti: qui si contano e basta,
 * nello stato restano due numeri.
 */
function useGroupProgress(token: string, groupBookingId: string | undefined, enabled: boolean): GroupProgress | null {
  const [progress, setProgress] = useState<GroupProgress | null>(null);
  useEffect(() => {
    if (!enabled || !token || !groupBookingId) {
      setProgress(null);
      return undefined;
    }
    let alive = true;
    fetchGroupBookingInfo(token, groupBookingId)
      .then((res) => {
        if (!alive) return;
        const apartments = res.success && Array.isArray(res.apartments) ? res.apartments : [];
        setProgress(apartments.length > 0
          ? { done: apartments.filter((a) => a.onlineCheckInCompleted).length, total: apartments.length }
          : null);
      })
      .catch(() => { if (alive) setProgress(null); });
    return () => { alive = false; };
  }, [enabled, token, groupBookingId]);
  return progress;
}

// ---- I riquadri ----------------------------------------------------------------

type TodoTone = 'due' | 'soft';

const TODO_BOX: Record<TodoTone, string> = {
  // Pesca col filo terracotta, come il "da pagare" delle email.
  due: 'border-nva-due-edge bg-nva-due-bg dark:bg-nva-due-bg-dark',
  // Crema col filo d'oro: c'è da fare, ma non ancora.
  soft: 'border-nva-gold bg-nva-cream dark:bg-nva-cream-dark',
};

// #9C5A22 sul pesca 4.7:1 (#F0A868 in scuro 7.8:1); l'oro scuro sul crema 5.2:1.
const TODO_LABEL: Record<TodoTone, string> = {
  due: 'text-nva-due-ink dark:text-nva-due-ink-dark',
  soft: 'text-nva-gold-text dark:text-nva-gold-dark',
};

const BADGE = 'text-[10.5px] font-extrabold uppercase leading-snug tracking-[0.14em]';
const DESC = 'm-0 text-[13px] leading-snug text-nva-muted dark:text-stone-300';

interface TodoBlockProps {
  tone: TodoTone;
  badge: React.ReactNode;
  /** A destra del badge: "Per 2 ospiti", "1 di 3 appartamenti". */
  meta?: React.ReactNode;
  title: React.ReactNode;
  desc?: React.ReactNode;
  children?: React.ReactNode;
}

/** Il riquadro del mockup Arrivo: badge e conteggio, titolo in Fraunces, testo, bottone. */
const TodoBlock: React.FC<TodoBlockProps> = ({ tone, badge, meta, title, desc, children }) => (
  <div className={cn('flex flex-col gap-2.5 rounded-l-[4px] rounded-r-2xl border-l-[3px] p-3.5', TODO_BOX[tone])}>
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
      <span className={cn(BADGE, TODO_LABEL[tone])}>{badge}</span>
      {meta ? <span className={cn('text-[12px] font-bold leading-snug', TODO_LABEL[tone])}>{meta}</span> : null}
    </div>
    <div className="flex flex-col gap-0.5">
      <h3 className={cn('m-0 font-display text-[19px] font-bold leading-tight', INK_TEXT)}>{title}</h3>
      {desc ? <p className={DESC}>{desc}</p> : null}
    </div>
    {children}
  </div>
);

/** "Codice porta pronto": la verifica dell'email si apre sotto, dentro la card. */
const DoorReady: React.FC<{ token: string; booking: GuestBooking }> = ({ token, booking }) => {
  const { t } = useTranslation();
  const door = useDoorCode(token, booking);
  const [open, setOpen] = useState(false);

  // Il codice arrivato (o nascosto) chiude la richiesta dell'email.
  useEffect(() => { setOpen(false); }, [door.revealed, token]);

  // Aperta la verifica, il bottone lascia il posto al campo (che prende il
  // focus): due "mostra il codice" uno sotto l'altro confondono.
  const showPanel = open || door.revealed;
  return (
    <CardShell className="flex flex-col gap-3 p-3.5">
      <div className="flex items-start gap-3">
        <IconCircle tone="ink"><KeyRound /></IconCircle>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className={cn('inline-flex items-center gap-1', BADGE, TODO_LABEL.soft)}>
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            {t('home.before.done')}
          </span>
          <h3 className={cn('m-0 font-display text-[19px] font-bold leading-tight', INK_TEXT)}>{t('home.before.codeReady')}</h3>
          <p className={DESC}>{t('home.before.codeReadyDesc')}</p>
        </div>
      </div>
      {!showPanel ? (
        <InkButton full onClick={() => setOpen(true)}>
          {t('home.before.codeReadyCta')}
        </InkButton>
      ) : (
        <DoorCodeReveal door={door} apartmentName={booking.apartmentName} idPrefix="home-todo-door" />
      )}
    </CardShell>
  );
};

/** Check-in fatto ma codice non ancora pronto (la tassa): solo la conferma. */
const CheckInDoneRow: React.FC = () => {
  const { t } = useTranslation();
  return (
    <CardShell className="flex min-h-[60px] items-center gap-3 px-3.5 py-3">
      <IconCircle><Check /></IconCircle>
      <h3 className={cn('m-0 min-w-0 flex-grow text-[15px] font-bold leading-snug', INK_TEXT)}>{t('home.before.checkinTitle')}</h3>
      <Pill className="self-center">{t('home.before.done')}</Pill>
    </CardShell>
  );
};

// ---- La sezione ----------------------------------------------------------------

const CheckInTodo: React.FC<CheckInTodoProps> = ({ state, token, booking, doorInQuickActions = false, className }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const progress = useGroupProgress(token, booking.groupBookingId, state.isGroup && !state.checkInDone);
  const titleId = 'home-todo-title';

  let checkIn: React.ReactNode = null;
  if (!state.checkInDone) {
    const meta = progress
      ? t('home.before.apartmentsDone', { done: progress.done, total: progress.total })
      : booking.numberOfGuests > 0 ? t('home.before.forGuests', { count: booking.numberOfGuests }) : null;
    if (state.checkInRedo && state.checkInWindowOpen) {
      // Il gestore l'ha rifiutato: va rifatto, col motivo e la nota.
      checkIn = (
        <TodoBlock
          tone="due"
          badge={t('checkInRedo.badge')}
          meta={meta}
          title={t('checkInRedo.title')}
          desc={t('checkInRedo.desc')}
        >
          <CheckInRedoNote redo={state.checkInRedo} />
          <InkButton to={state.checkInPath} full>{t('checkInRedo.cta')}</InkButton>
        </TodoBlock>
      );
    } else if (!state.checkInWindowOpen && state.checkInOpensOn) {
      // Più di 7 giorni all'arrivo: si dice quando si apre, niente bottone.
      checkIn = (
        <TodoBlock
          tone="soft"
          badge={t('home.before.required')}
          meta={meta}
          title={t('home.before.checkinTitle')}
          desc={t('home.before.opensDesc')}
        >
          <p className={cn('m-0 flex items-center gap-2 text-[13.5px] font-bold leading-snug', INK_TEXT)}>
            <CalendarClock className="h-[18px] w-[18px] flex-shrink-0 text-nva-gold-text dark:text-nva-gold-dark" aria-hidden="true" />
            {t('home.before.opensOn', {
              date: formatDayKey(state.checkInOpensOn, lang, { weekday: 'long', day: 'numeric', month: 'long' }),
            })}
          </p>
        </TodoBlock>
      );
    } else {
      checkIn = (
        <TodoBlock
          tone="due"
          badge={state.isArrivalDay ? t('home.before.urgent') : t('home.before.required')}
          meta={meta}
          title={t('home.before.checkinTitle')}
          desc={t('home.before.checkinDesc')}
        >
          <InkButton to={state.checkInPath} full>{t('home.before.checkinCta')}</InkButton>
        </TodoBlock>
      );
    }
  } else if (state.taxDue || !state.doorCodeAvailable) {
    checkIn = <CheckInDoneRow />;
  } else if (!doorInQuickActions) {
    checkIn = <DoorReady token={token} booking={booking} />;
  }

  // La tassa si paga prima di arrivare (riquadro condiviso con la vista S).
  const tax = state.taxDue ? <TouristTaxDue booking={booking} variant="before" titleAs="h3" /> : null;
  if (!checkIn && !tax) return null;

  return (
    <Section labelledBy={titleId} className={className}>
      <SectionTitle id={titleId} eyebrow={t('home.before.todoEyebrow')} title={t('home.before.todoTitle')} />
      {checkIn}
      {tax}
    </Section>
  );
};

export default CheckInTodo;
